import React, { useState, useEffect, useCallback } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import { analyticsApi } from '../services/api';
import './Analytics.css';

interface DailyStats {
  date: string;
  total_calls: number;
  answered_calls: number;
  avg_duration: number;
}

interface SourceStats {
  utm_source: string;
  utm_medium: string;
  utm_campaign: string;
  calls: number;
  conversions: number;
  avg_duration: number;
}

const Analytics: React.FC = () => {
  const [dailyStats, setDailyStats] = useState<DailyStats[]>([]);
  const [sourceStats, setSourceStats] = useState<SourceStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState({
    startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });

  const fetchAnalytics = useCallback(async () => {
    try {
      setLoading(true);

      const [dailyResponse, sourceResponse] = await Promise.all([
        analyticsApi.getDailyReport(dateRange.startDate, dateRange.endDate),
        analyticsApi.getSourcesReport(dateRange.startDate, dateRange.endDate)
      ]);

      setDailyStats(dailyResponse.data);
      setSourceStats(sourceResponse.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const formatDuration = (seconds: number) => {
    if (!seconds) return '0:00';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.round(seconds % 60);
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const calculateConversionRate = (conversions: number, calls: number) => {
    return calls > 0 ? ((conversions / calls) * 100).toFixed(1) : '0.0';
  };

  if (loading) {
    return <div className="analytics-loading">Loading analytics...</div>;
  }

  return (
    <div className="analytics-page">
      <div className="analytics-header">
        <h1>Analytics & Reports</h1>
        <div className="date-filters">
          <input
            type="date"
            value={dateRange.startDate}
            onChange={(e) => setDateRange(prev => ({ ...prev, startDate: e.target.value }))}
          />
          <input
            type="date"
            value={dateRange.endDate}
            onChange={(e) => setDateRange(prev => ({ ...prev, endDate: e.target.value }))}
          />
        </div>
      </div>

      <div className="analytics-content">
        <div className="chart-section">
          <div className="chart-card">
            <h3>Daily Call Volume</h3>
            <ResponsiveContainer width="100%" height={400}>
              <LineChart data={dailyStats}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tickFormatter={formatDate} />
                <YAxis />
                <Tooltip labelFormatter={formatDate} />
                <Line
                  type="monotone"
                  dataKey="total_calls"
                  stroke="#3498db"
                  strokeWidth={2}
                  name="Total Calls"
                />
                <Line
                  type="monotone"
                  dataKey="answered_calls"
                  stroke="#27ae60"
                  strokeWidth={2}
                  name="Answered Calls"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>

          <div className="chart-card">
            <h3>Average Call Duration by Day</h3>
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={dailyStats}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tickFormatter={formatDate} />
                <YAxis />
                <Tooltip
                  labelFormatter={formatDate}
                  formatter={(value: number) => [formatDuration(value), 'Duration']}
                />
                <Bar dataKey="avg_duration" fill="#f39c12" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="table-section">
          <div className="table-card">
            <h3>Traffic Source Performance</h3>
            <div className="table-container">
              <table className="analytics-table">
                <thead>
                  <tr>
                    <th>Source</th>
                    <th>Medium</th>
                    <th>Campaign</th>
                    <th>Calls</th>
                    <th>Conversions</th>
                    <th>Conversion Rate</th>
                    <th>Avg Duration</th>
                  </tr>
                </thead>
                <tbody>
                  {sourceStats.map((source, index) => (
                    <tr key={index}>
                      <td>{source.utm_source}</td>
                      <td>{source.utm_medium || '-'}</td>
                      <td>{source.utm_campaign || '-'}</td>
                      <td>{source.calls}</td>
                      <td>{source.conversions}</td>
                      <td>{calculateConversionRate(source.conversions, source.calls)}%</td>
                      <td>{formatDuration(source.avg_duration)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="table-card">
            <h3>Daily Summary</h3>
            <div className="table-container">
              <table className="analytics-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Total Calls</th>
                    <th>Answered</th>
                    <th>Answer Rate</th>
                    <th>Avg Duration</th>
                  </tr>
                </thead>
                <tbody>
                  {dailyStats.map((day, index) => (
                    <tr key={index}>
                      <td>{formatDate(day.date)}</td>
                      <td>{day.total_calls}</td>
                      <td>{day.answered_calls}</td>
                      <td>
                        {day.total_calls > 0
                          ? ((day.answered_calls / day.total_calls) * 100).toFixed(1)
                          : '0.0'
                        }%
                      </td>
                      <td>{formatDuration(day.avg_duration)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Analytics;